// v4.35 - WEBSITE-SIGNIN-NO-BANNER-01 - 2026-10-29 - one "Cancel" link under the sign-in form, to "/" (existing key common:actions.cancel). The sign-in form itself is unchanged.
// v4.34 - WEBSITE-DISABLE-01 - 2026-10-28 - Temporarily disabled by Branko: the "Don't have an account? Create one" link (row 776) and the "Or continue with" divider + Continue with Google / Microsoft buttons and their handlers (row 785) are removed from the rendered page. The /sso-callback route stays in App.tsx.
// v3.93 - Dark mode support on public pages: theme-aware bgcolor + transparent logos - 2026-03-19
// v2.69 - Handle Clerk 2FA (needs_second_factor) with email code verification - 2026-02-10
// v2.68 - On session_exists, use setActive to activate existing session - 2026-02-05
// v2.67 - Fix sign-in button not working after stale session clear - 2026-02-05
// v2.66 - Fix session_exists with stale session by signing out first - 2026-02-05
// v2.64 - Enhanced Clerk error logging for debugging sign-in failures - 2026-02-05
// v2.61 - Better error handling for session conflicts, auto-redirect if signed in - 2026-02-05
// v2.59 - Show success message after registration redirect - 2026-02-04
// v2.55 - Complete rewrite with Clerk authentication - 2026-02-04
import React, { useState, useEffect } from 'react';
import MyLojicLogo from '../components/MyLojicLogo';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useSignIn, useAuth, useClerk } from '@clerk/clerk-react';
import { useTheme } from '@mui/material/styles'; // v3.93 - dark mode detection
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { colors } from '../theme/colors';
import { isRejectedClerkSession } from '../services/authGate';

// v4.34 - WEBSITE-DISABLE-01 (row 785) - MicrosoftIcon removed with the social sign-in buttons (temporarily disabled by Branko 2026-10-28).

const Login: React.FC = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme(); // v3.93 - dark mode detection
  const isDark = muiTheme.palette.mode === 'dark';
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isSignedIn, isLoaded: authLoaded, sessionId } = useAuth();
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn();
  const { signOut, client } = useClerk(); // v2.68 - Added client for session access

  // v2.59 - Check if redirected from registration
  const justRegistered = searchParams.get('registered') === 'true';
  const justVerified = searchParams.get('verified') === 'true';

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    justRegistered || justVerified ? t('login.registrationSuccess') : null
  );

  // v2.69 - 2FA state
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Redirect if already signed in
  useEffect(() => {
    if (authLoaded && isSignedIn && !isRejectedClerkSession(sessionId)) {
      navigate('/auth-landing', { replace: true });
    }
  }, [authLoaded, isSignedIn, sessionId, navigate]);

  // Email/Password sign-in
  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleEmailSignIn called, signInLoaded:', signInLoaded, 'signIn:', !!signIn, 'isLoading:', isLoading);

    if (!signInLoaded || !signIn) {
      console.log('Sign-in not ready yet');
      return;
    }

    if (isLoading) {
      console.log('Already loading, ignoring click');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.create({
        identifier: email,
        password: password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/auth-landing');
      } else if (result.status === 'needs_second_factor') {
        // v2.69 - Handle 2FA email code verification
        console.log('needs_second_factor: Preparing email code verification');
        // v2.69 - Clerk types only declare phone_code, but API supports email_code
        await signIn.prepareSecondFactor({ strategy: 'email_code' as any });
        setNeedsSecondFactor(true);
        setIsLoading(false);
        setSuccessMessage(t('verification.subtitle', { email }));
      } else if (result.status === 'needs_first_factor') {
        setError(t('login.errors.generic'));
      }
    } catch (err: any) {
      // v2.64 - Enhanced error logging for debugging
      console.error('Sign-in error:', err);
      console.error('Error type:', typeof err);
      console.error('Error constructor:', err?.constructor?.name);
      console.error('Error message:', err?.message);
      console.error('Error errors array:', err?.errors);
      console.error('Error clerkError:', err?.clerkError);
      console.error('Error status:', err?.status);
      console.error('Full error JSON:', JSON.stringify(err, null, 2));

      // Map Clerk error codes to user-friendly messages
      const errorCode = err.errors?.[0]?.code || err.code;
      const errorMessage = err.errors?.[0]?.message || err.message || '';
      const longMessage = err.errors?.[0]?.longMessage || '';

      console.error('Parsed error:', { errorCode, errorMessage, longMessage });

      // v2.66 - Handle session conflict (stale session on Clerk server but not in React)
      if (errorCode === 'session_exists' || errorMessage.includes('already signed in') || errorMessage.includes('active session')) {
        // There's a stale session - if isSignedIn is true, redirect; otherwise sign out first
        if (isSignedIn && !isRejectedClerkSession(sessionId)) {
          console.log('session_exists: User is signed in, redirecting to auth-landing');
          navigate('/auth-landing', { replace: true });
        } else {
          // v2.68 - Session exists on server but not in React - try to activate existing session
          console.log('session_exists but isSignedIn=false: Trying to activate existing session...');
          try {
            // Check if there's an existing session in the client we can activate
            const sessions = client?.sessions?.filter((session) => !isRejectedClerkSession(session.id));
            console.log('Available sessions:', sessions?.length || 0);

            if (sessions && sessions.length > 0) {
              // Activate the most recent session
              const activeSession = sessions[0];
              console.log('Activating existing session:', activeSession.id);
              await setActive({ session: activeSession.id });
              navigate('/auth-landing', { replace: true });
              return;
            }

            // No sessions available locally - sign out server session and retry
            console.log('No local sessions, signing out server session...');
            await signOut();
            console.log('signOut completed successfully');
            setIsLoading(false);
            setError('Your previous session has expired. Please sign in again.');
            return;
          } catch (activateErr) {
            console.error('Failed to activate or clear session:', activateErr);
            setIsLoading(false);
            setError('Session conflict. Please clear your browser cookies and try again.');
            return;
          }
        }
        return;
      }

      switch (errorCode) {
        case 'form_identifier_not_found':
          setError(t('login.errors.accountNotFound'));
          break;
        case 'form_password_incorrect':
          setError(t('login.errors.invalidCredentials'));
          break;
        case 'too_many_attempts':
          setError(t('login.errors.tooManyAttempts'));
          break;
        case 'identifier_already_signed_in':
          if (isRejectedClerkSession(sessionId)) {
            await signOut();
            setError('Your previous session has expired. Please sign in again.');
          } else {
            navigate('/auth-landing', { replace: true });
          }
          return;
        default:
          // v2.64 - Show actual error message if available for debugging
          const displayError = longMessage || errorMessage || t('login.errors.generic');
          console.error('Setting error display:', displayError);
          setError(displayError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // v2.69 - Handle 2FA email code submission
  const handleSecondFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInLoaded || !signIn || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'email_code' as any,
        code: verificationCode,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/auth-landing');
      } else {
        console.error('Unexpected 2FA result status:', result.status);
        setError(t('login.errors.generic'));
      }
    } catch (err: any) {
      console.error('2FA verification error:', err);
      const errorCode = err.errors?.[0]?.code || err.code;
      if (errorCode === 'form_code_incorrect') {
        setError(t('verification.errors.invalidCode'));
      } else if (errorCode === 'verification_expired') {
        setError(t('verification.errors.expiredCode'));
      } else {
        setError(t('verification.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // v2.69 - Resend 2FA email code
  const handleResendCode = async () => {
    if (!signInLoaded || !signIn || isLoading) return;

    try {
      setIsLoading(true);
      await signIn.prepareSecondFactor({ strategy: 'email_code' as any });
      setSuccessMessage(t('verification.resendSuccess'));
      setError(null);
    } catch (err) {
      console.error('Resend 2FA code error:', err);
      setError(t('verification.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // v4.34 - WEBSITE-DISABLE-01 (row 785) - Google / Microsoft sign-in handlers removed with their buttons (temporarily disabled by Branko 2026-10-28).

  // Show loading while Clerk initializes
  if (!authLoaded || !signInLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress sx={{ color: colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2 }}>
          {/* Logo - v4.07 - inline SVG component */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <MyLojicLogo isDark={isDark} height={48} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            {t('login.title')}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              textAlign: 'center',
              mb: 4,
            }}
          >
            {t('login.subtitle')}
          </Typography>

          {/* v2.59 - Success Alert (after registration) */}
          {successMessage && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setSuccessMessage(null)}
            >
              {successMessage}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, bgcolor: colors.status.errorLight }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* v2.69 - 2FA Verification Code Form */}
          {needsSecondFactor ? (
            <Box component="form" onSubmit={handleSecondFactor}>
              <TextField
                fullWidth
                label={t('verification.codeLabel')}
                placeholder={t('verification.codePlaceholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                autoComplete="one-time-code"
                inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || !verificationCode}
                sx={{
                  bgcolor: colors.primary.main,
                  color: colors.primary.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.primary.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                    {t('verification.verifying')}
                  </>
                ) : (
                  t('verification.submitButton')
                )}
              </Button>

              <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 2, color: colors.text.secondary }}
              >
                {t('verification.resendCode')}{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('verification.resendButton')}
                </Link>
              </Typography>
            </Box>
          ) : (
            <>
              {/* Login Form */}
              <Box component="form" onSubmit={handleEmailSignIn}>
                {/* Email Field */}
                <TextField
                  fullWidth
                  label={t('login.emailLabel')}
                  placeholder={t('login.emailPlaceholder')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="email"
                  autoFocus
                  sx={{ mb: 2 }}
                  inputProps={{
                    'aria-describedby': error ? 'login-error' : undefined,
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  label={t('login.passwordLabel')}
                  placeholder={t('login.passwordPlaceholder')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="current-password"
                  sx={{ mb: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: 'right', mb: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      color: colors.primary.main,
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </Box>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading || !email || !password}
                  sx={{
                    bgcolor: colors.primary.main,
                    color: colors.primary.contrastText,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: colors.primary.dark },
                    '&:disabled': { bgcolor: colors.border.main },
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                      {t('login.signingIn')}
                    </>
                  ) : (
                    t('login.submitButton')
                  )}
                </Button>
              </Box>

              {/* v4.35 - WEBSITE-SIGNIN-NO-BANNER-01: Cancel returns to the home page */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/"
                  data-testid="login-cancel"
                  sx={{
                    color: colors.text.secondary,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('common:actions.cancel')}
                </Link>
              </Box>

              {/* v4.34 - WEBSITE-DISABLE-01 - temporarily disabled by Branko 2026-10-28: "Or continue with" divider + Google / Microsoft buttons removed (row 785); "Don't have an account? Create one" link removed (row 776). */}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
